// @flow

import {
  ALL_SUPPORTED_LANGUAGES,
  LANGUAGE_BY_COUNTRY,
  LANGUAGE_ENGLISH,
  LANGUAGES_BY_COUNTRY,
} from './enums/language-by-country.enum';
import intl from 'react-intl-universal';
import { locales } from './locales';
import { overwrite } from './overwrite';
import { handleError } from '../alert-handler';
import { getCurrentLanguage, getDefaultCountry } from '@tectonics/controller/language/language-service';

export function translate(translatePath: string, translateParams: ?Object): string {
  return getTranslation(intl.get.bind(intl), translatePath, translateParams);
}

export function translateHTML(translatePath: string, translateParams: ?Object): string {
  return getTranslation(intl.getHTML.bind(intl), translatePath, translateParams);
}

function getTranslation(
  translateFn: (path: string, params: ?Object) => string,
  translatePath: string,
  translateParams: ?Object
): string {
  const translatedMsg = translateFn(translatePath, translateParams);
  const translateMsgNotExist = translatedMsg === '';

  if (translateMsgNotExist) {
    const { currentLocale } = intl.getInitOptions();
    handleError(Error(`Translation key '${translatePath}' not found for language '${currentLocale}'`));
    return translatePath;
  }

  return translatedMsg;
}


export async function initReactIntl(country: string) {
  let currentLocale = getLanguageByCountry(country);
  const overwriteLocale = getLocalesWithOverwriteTranslationsByCountry(currentLocale, country);
  const languageCode: string = getCurrentLanguage();
  const defaultCountry: string = getDefaultCountry();

  const languageIsSupported: boolean = checkIfTheCountrySupportsTheLanguage(defaultCountry, languageCode);

  if (languageIsSupported) {
    currentLocale = languageCode;
  }

  await intl.init({
    currentLocale,
    locales: overwriteLocale,
  });
}

function checkIfTheCountrySupportsTheLanguage(country: string, languageCode: string): boolean {
  if (!languageCode) {
    return false;
  }

  const languagesSupportedList = LANGUAGES_BY_COUNTRY[country];
  if (!languagesSupportedList) {
    return false;
  }

  return languagesSupportedList.some((lang) => lang.code === languageCode);
}

function getLanguageByCountry(country: string): string {

  const browserLanguage = getBrowserLanguage();
  const supportedLanguages: string[] = ALL_SUPPORTED_LANGUAGES;
  const isSupportedLanguage = supportedLanguages.includes(browserLanguage);

  const defaultLanguage = isSupportedLanguage ? browserLanguage : LANGUAGE_ENGLISH;

  return LANGUAGE_BY_COUNTRY[country] || defaultLanguage;
}

function getBrowserLanguage(): string {
  const browserLanguage = navigator?.languages[0] || navigator.language;
  return browserLanguage.split('-')[0];
}

function getLocalesWithOverwriteTranslationsByCountry(language, country): Object {
  const overwriteTranslation = getOverwriteTranslationByCountry(country);

  if (!overwriteTranslation) return locales;

  const localesByLanguage = locales[language] || null;
  const localesByLanguageKeys = Object.keys(localesByLanguage);

  const localesWithOverwriteKeys = localesByLanguageKeys.reduce((target: Object, localeKey: string) => {
    const localeByKey = localesByLanguage[localeKey];
    const overwriteTranslationByKey = overwriteTranslation[localeKey] || null;

    target[localeKey] = overwriteTranslationByKey
      ? {...localeByKey, ...overwriteTranslationByKey}
      : localeByKey;

    return target;
  }, {});

  return {...locales, [language]: localesWithOverwriteKeys};
}

function getOverwriteTranslationByCountry(country: string): ?Object {
  return overwrite[country] || null;
}
